import React, { useContext, useState, useEffect, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { InventoryItem } from "../../external";
import ImageCarousel from "../image-carousel";
import YieldCalculation from "../yield/yield-calculation";
import { UserAuthenticatedContext } from "../profile";
import { useMediaQuery } from "react-responsive";
import Card from "react-bootstrap/Card";
import Nav from "react-bootstrap/Nav";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";

interface InventoryInboundModalProps {
  index: number;
  isModalOpen: boolean;
  handleCloseModal: () => void;
  values: InventoryItem[];
  setValues: (values: InventoryItem[]) => void;
}

const InventoryInboundModal: React.FC<InventoryInboundModalProps> = ({
  index,
  isModalOpen,
  handleCloseModal,
  values,
  setValues,
}) => {
  const userAuthenticatedContext = useContext(UserAuthenticatedContext);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [activeTab, setActiveTab] = useState<string>("details");
  const [notes, setNotes] = useState<string>("");
  const [originalNotes, setOriginalNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [notesSaveSuccess, setNotesSaveSuccess] = useState<boolean | null>(
    null
  );

  const item = useMemo(() => values[index] || {}, [values, index]);

  const hasImages = item.images && item.images.length > 0;

  useEffect(() => {
    if (item) {
      setNotes(item.comments || "");
      setOriginalNotes(item.comments || "");
    }
  }, [item]);

  const saveNotes = async () => {
    if (notes === originalNotes) return;

    setIsSaving(true);
    setNotesSaveSuccess(null);

    try {
      if (values[index]) {
        const updatedValues = [...values];
        updatedValues[index] = {
          ...updatedValues[index],
          comments: notes,
        };

        // Save to localStorage
        setValues(updatedValues);

        const result = { success: true };

        if (result.success) {
          setOriginalNotes(notes);
          setNotesSaveSuccess(true);
        } else {
          setNotesSaveSuccess(false);
        }
      } else {
        setNotesSaveSuccess(false);
      }
    } catch (error) {
      console.error("Failed to save notes:", error);
      setNotesSaveSuccess(false);
    } finally {
      setIsSaving(false);

      // Clear success/error message after a delay
      if (notesSaveSuccess !== null) {
        setTimeout(() => {
          setNotesSaveSuccess(null);
        }, 3000);
      }
    }
  };

  // Group item details for better organization
  const detailGroups = [
    {
      title: "Basic Info",
      fields: [
        { label: "Item ID", value: item.id },
        { label: "Legacy PM ID", value: item.legacyPMID },
        { label: "Description", value: item.description },
      ],
    },
    {
      title: "Client Info",
      fields: [
        { label: "Client", value: item.clientId },
        { label: "Sub Client", value: item.subClientId },
        { label: "Client Item ID", value: item.clientItemId },
        { label: "Catalogue Item ID", value: item.catalogItemId },
      ],
    },
    {
      title: "Inventory & Logistics",
      fields: [
        { label: "Quantity", value: item.quantity },
        { label: "Boxes", value: item.boxes },
        { label: "Pallet", value: item.pallet },
        { label: "Load Reference ID", value: item.loadReferenceId },
        { label: "Expected Arrival Date", value: item.expectedArrivalDate },
      ],
    },
  ];

  // Notes tab content
  const renderNotesTab = () => (
    <Card className="mb-3 shadow-sm">
      <Card.Header className="py-2 bg-light d-flex justify-content-between align-items-center">
        <span className="fw-bold">Item Notes</span>
        <div>
          {notesSaveSuccess === true && (
            <span className="text-success me-2 small">Saved successfully</span>
          )}
          {notesSaveSuccess === false && (
            <span className="text-danger me-2 small">Failed to save</span>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={saveNotes}
            disabled={isSaving || notes === originalNotes}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <Form.Group>
          <Form.Control
            as="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={isMobile ? 8 : 12}
            placeholder="Add notes about this item here..."
            className="mb-2"
            style={{ fontSize: isMobile ? "14px" : "16px" }}
          />
          <small className="text-muted">
            Last updated: {new Date().toLocaleString()}
          </small>
        </Form.Group>
      </Card.Body>
    </Card>
  );

  return (
    <Modal show={isModalOpen} onHide={handleCloseModal} fullscreen={true}>
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="fs-5">Item Details</Modal.Title>
      </Modal.Header>

      {/* Images Section - always visible at top */}
      {hasImages && (
        <div className="p-2 text-center bg-light">
          <ImageCarousel
            imageUrls={item?.images?.map((x) => x.imageUrl) || []}
            maxWidth={"100%"}
          />
        </div>
      )}

      {/* Navigation Tabs for Mobile */}
      <Nav
        variant="tabs"
        className="px-2 pt-2"
        activeKey={activeTab}
        onSelect={(k) => k && setActiveTab(k)}
      >
        <Nav.Item>
          <Nav.Link eventKey="details" className="py-2 px-3">
            Details
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="yield" className="py-2 px-3">
            Yield
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="notes" className="py-2 px-3">
            Notes
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Modal.Body className="p-2">
        {/* Mobile and desktop views now both use tabs for consistency */}
        <>
          {activeTab === "yield" && (
            <YieldCalculation
              inventoryId={item.id}
              authenticatedUserToken={userAuthenticatedContext.token as string}
            />
          )}

          {activeTab === "details" && (
            <>
              {detailGroups.map((group, groupIndex) => (
                <Card key={groupIndex} className="mb-3 shadow-sm">
                  <Card.Header className="py-2 bg-light">
                    <span className="fw-bold">{group.title}</span>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <table className="table table-sm m-0">
                      <tbody className="small">
                        {group.fields.map((field, fieldIndex) => (
                          <tr key={fieldIndex}>
                            <td
                              className="text-muted ps-3"
                              style={{ width: "40%" }}
                            >
                              {field.label}
                            </td>
                            <td className="fw-semibold text-end pe-3">
                              {field.value || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card.Body>
                </Card>
              ))}
            </>
          )}

          {activeTab === "notes" && renderNotesTab()}
        </>
      </Modal.Body>

      <Modal.Footer className="py-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCloseModal}
          className="px-4"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InventoryInboundModal;
